import { useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { api } from "../../lib/apiClient";
import {
  describeAuthError,
  errorsOnBlur,
  errorsOnSubmit,
  errorsOnType,
  firstErrorField,
  validateConfirm,
  validatePassword,
  type FieldErrors,
  type ShownErrors,
} from "../../lib/authForm";
import { PasswordChecklist, Spinner } from "../../components/ui";
import {
  AuthBody,
  AuthField,
  AuthFooter,
  AuthHeading,
  AuthPending,
  FieldError,
} from "../../components/auth-card";
import { ArrowRightIcon } from "../../components/icons";
import { PasswordInput } from "../../components/password-input";

export const Route = createFileRoute("/auth/reset")({
  validateSearch: z.object({ token: z.string().optional() }),
  component: Reset,
  pendingComponent: AuthPending,
});

type ResetField = "password" | "confirm";

function Reset() {
  const { token } = Route.useSearch();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<ShownErrors<ResetField>>({});
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const refs = {
    password: useRef<HTMLInputElement>(null),
    confirm: useRef<HTMLInputElement>(null),
  };

  const check = (field: ResetField, value: string) =>
    field === "password" ? validatePassword(value) : validateConfirm(password, value);

  function edit(field: ResetField, value: string) {
    if (field === "password") setPassword(value);
    else setConfirm(value);
    setErrors((prev) => errorsOnType(prev, field, check(field, value)));
    setFailure(null);
  }

  function leave(field: ResetField) {
    const value = field === "password" ? password : confirm;
    setErrors((prev) => errorsOnBlur(prev, field, value, check(field, value)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    const found: FieldErrors<ResetField> = {};
    const weak = validatePassword(password);
    if (weak) found.password = weak;
    const mismatch = validateConfirm(password, confirm);
    if (mismatch) found.confirm = mismatch;
    const first = firstErrorField(found);
    setErrors(errorsOnSubmit(found));
    setFailure(null);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    setBusy(true);
    try {
      await api("/v1/auth/password/reset", {
        body: { token, newPassword: password },
        auth: false,
      });
      setDone(true);
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  }

  const footer = (
    <AuthFooter prompt="Remembered it?">
      <Link to="/auth/signin" className="rago-auth-switch">
        Back to sign in
        <ArrowRightIcon size={13} bold />
      </Link>
    </AuthFooter>
  );

  if (!token) {
    return (
      <>
        <AuthBody>
          <AuthHeading title="Choose a new password">
            This page needs the link from your reset email.
          </AuthHeading>
          <Link to="/auth/forgot" className="rago-btn rago-btn-block rago-btn-lg">
            Request a new link
          </Link>
        </AuthBody>
        {footer}
      </>
    );
  }

  if (done) {
    return (
      <AuthBody>
        <AuthHeading title="Password updated">
          You've been signed out everywhere. Sign in with your new password.
        </AuthHeading>
        <Link to="/auth/signin" className="rago-btn rago-btn-block rago-btn-lg">
          Sign in
        </Link>
      </AuthBody>
    );
  }

  return (
    <>
      <AuthBody>
        <AuthHeading title="Choose a new password">This signs you out everywhere.</AuthHeading>
        <form onSubmit={submit} noValidate className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <AuthField
              id="password"
              label="New password"
              error={errors.password?.message}
              alert={errors.password?.alert}
            >
              <PasswordInput
                ref={refs.password}
                id="password"
                size="lg"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(v) => edit("password", v)}
                onBlur={() => leave("password")}
                invalid={Boolean(errors.password)}
                describedBy={errors.password ? "password-error" : undefined}
              >
                <PasswordChecklist password={password} heading={false} />
              </PasswordInput>
            </AuthField>
            <AuthField
              id="confirm"
              label="Confirm password"
              error={errors.confirm?.message}
              alert={errors.confirm?.alert}
            >
              <PasswordInput
                ref={refs.confirm}
                id="confirm"
                size="lg"
                autoComplete="new-password"
                value={confirm}
                onChange={(v) => edit("confirm", v)}
                onBlur={() => leave("confirm")}
                invalid={Boolean(errors.confirm)}
                describedBy={errors.confirm ? "confirm-error" : undefined}
              />
            </AuthField>
            {failure ? (
              <FieldError id="reset-error" alert>
                {describeAuthError(failure, "reset").message}{" "}
                <Link to="/auth/forgot" className="font-semibold underline underline-offset-2">
                  Request a new link
                </Link>
              </FieldError>
            ) : null}
          </div>
          <button type="submit" className="rago-btn rago-btn-block rago-btn-lg" disabled={busy}>
            {busy ? <Spinner /> : null}
            Set password
          </button>
        </form>
      </AuthBody>
      {footer}
    </>
  );
}
